import { useState, useMemo, useCallback, useRef, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  Shield, ShieldCheck, Eye, EyeOff, Archive, Star,
  Search, ChevronDown, Check, X, LogIn, Loader2,
  Phone, Scissors, Users, Plus,
  ChevronLeft, StickyNote, LogOut,
  CheckSquare, Square, MinusSquare,
} from "lucide-react";
import "@/styles/brand-kit.css";
import "./admin.css";

// ── Types ───────────────────────────────────────────────────────────────────

type CurationStatus = "draft" | "published" | "hidden" | "archived";
type ContentType = "call" | "win" | "clip" | "sms" | "email" | "video";
type FeaturedLabel = "editors_pick" | "best_of_week" | "top_performer" | null;

interface CurationRecord {
  content_id: string;
  content_type: ContentType;
  status: CurationStatus;
  featured: boolean;
  featured_label: FeaturedLabel;
  title_override: string | null;
  description_override: string | null;
  tags_override: string[] | null;
  category_override: string | null;
  internal_notes: string | null;
  published_at: string | null;
  published_by: string | null;
  hidden_at: string | null;
  hidden_by: string | null;
  archived_at: string | null;
  archived_by: string | null;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}


interface ContentItem {
  id: string;
  contact_name?: string;
  business_name?: string;
  call_summary?: string;
  duration?: number;
  call_type?: string;
  recording_url?: string;
  created_at?: string;
  quote?: string;
  title?: string;
  summary?: string;
  platform?: string;
  account?: string;
  author_name?: string;
  media_url?: string;
  starred?: boolean;
  tags?: string[];
  start_sec?: number;
  end_sec?: number;
  note?: string;
  transcript_excerpt?: string;
  curation?: CurationRecord | null;
}

// ── Constants ───────────────────────────────────────────────────────────────

const CONTENT_TYPES: { key: ContentType; label: string; icon: typeof Phone }[] = [
  { key: "call", label: "Calls", icon: Phone },
  { key: "win", label: "Wins", icon: Star },
  { key: "clip", label: "Clips", icon: Scissors },
];

type Tone = "neutral" | "success" | "info" | "error" | "brand" | "teal" | "violet";

// Tones are defined in admin.css from brand-kit tokens.
const STATUS_CONFIG: Record<CurationStatus, { label: string; tone: Tone }> = {
  draft:     { label: "Draft",     tone: "neutral" },
  published: { label: "Published", tone: "success" },
  hidden:    { label: "Hidden",    tone: "info" },
  archived:  { label: "Archived",  tone: "error" },
};

const FEATURED_LABELS: { value: FeaturedLabel; label: string }[] = [
  { value: null, label: "None" },
  { value: "editors_pick", label: "Editor's Pick" },
  { value: "best_of_week", label: "Best of the Week" },
  { value: "top_performer", label: "Top Performer" },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: CurationStatus) {
  const c = STATUS_CONFIG[status];
  return (
    <span className="adm-badge" data-tone={c.tone}>
      <span className="adm-dot" />
      {c.label}
    </span>
  );
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDuration(sec: number | undefined) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function truncate(s: string | null | undefined, len: number) {
  if (!s) return "";
  return s.length > len ? s.slice(0, len) + "..." : s;
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { user, isAuthenticated, isLoading: authLoading, canCurate, isAdmin } = useAuth();

  return (
    <div className="brand-kit adm min-h-screen">
      {authLoading ? (
        <div className="min-h-screen grid place-items-center">
          <Loader2 className="w-6 h-6 animate-spin text-bk-muted" />
        </div>
      ) : !isAuthenticated ? (
        <AuthCard icon={Shield} title="Admin Dashboard" body="Sign in with your @fello.ai account.">
          <a href="/auth/google" className="adm-btn adm-btn-primary mt-6">
            <LogIn /> Sign in with Google
          </a>
        </AuthCard>
      ) : !canCurate ? (
        <AuthCard icon={ShieldCheck} title="Access Denied" body="You don't have curator permissions. Contact an admin." />
      ) : (
        <AdminShell userName={user?.name ?? "Admin"} userEmail={user?.email ?? ""} isAdmin={isAdmin} />
      )}
    </div>
  );
}

function AuthCard({ icon: Icon, title, body, children }: {
  icon: typeof Shield; title: string; body: string; children?: ReactNode;
}) {
  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="text-center max-w-sm bg-bk-white rounded-bk-lg shadow-bk-card border border-bk-border p-10">
        <Icon className="w-10 h-10 mx-auto mb-4 text-bk-gray-3" />
        <h1 className="text-bk-md font-semibold text-bk-heading mb-1">{title}</h1>
        <p className="text-bk-sm text-bk-muted">{body}</p>
        {children}
      </div>
    </div>
  );
}

// ── Admin Shell ─────────────────────────────────────────────────────────────

function AdminShell({ userName, userEmail, isAdmin }: { userName: string; userEmail: string; isAdmin: boolean }) {
  const [activeType, setActiveType] = useState<ContentType>("call");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<CurationStatus | "all">("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeView, setActiveView] = useState<"content" | "access">("content");
  const listRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  // ── Data fetching ───────────────────────────────────────────────────────

  const { data: curationRecords = [] } = useQuery<CurationRecord[]>({
    queryKey: ["curation-records", activeType],
    queryFn: async () => {
      const res = await fetch(`/api/curation/${activeType}`);
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 10_000,
  });

  const { data: sourceContent = [], isLoading: sourceLoading } = useQuery<ContentItem[]>({
    queryKey: ["admin-source", activeType],
    queryFn: async () => {
      if (activeType === "call") {
        const res = await fetch("/api/admin/calls");
        if (!res.ok) return [];
        return res.json();
      }
      if (activeType === "win") {
        const res = await fetch("/api/wins-v2");
        if (!res.ok) return [];
        return res.json();
      }
      if (activeType === "clip") {
        const res = await fetch("/api/clips");
        if (!res.ok) return [];
        return res.json();
      }
      return [];
    },
    staleTime: 30_000,
  });

  const missingIds = useMemo(() => {
    const sourceIds = new Set(sourceContent.map((i) => String(i.id)));
    return curationRecords
      .filter((r) => !sourceIds.has(r.content_id))
      .map((r) => r.content_id);
  }, [sourceContent, curationRecords]);

  const { data: missingDetails = [] } = useQuery<ContentItem[]>({
    queryKey: ["admin-missing", activeType, missingIds],
    queryFn: async () => {
      if (!missingIds.length) return [];
      if (activeType === "call") {
        const results: ContentItem[] = [];
        for (let i = 0; i < missingIds.length; i += 20) {
          const batch = missingIds.slice(i, i + 20);
          const fetched = await Promise.all(
            batch.map(async (id) => {
              const res = await fetch(`/api/calls/${id}`);
              if (!res.ok) return null;
              return res.json() as Promise<ContentItem>;
            })
          );
          for (const item of fetched) {
            if (item) results.push(item);
          }
        }
        return results;
      }
      return [];
    },
    enabled: missingIds.length > 0,
    staleTime: 60_000,
  });

  const curationMap = useMemo(() => {
    const map = new Map<string, CurationRecord>();
    for (const r of curationRecords) map.set(r.content_id, r);
    return map;
  }, [curationRecords]);

  const mergedItems = useMemo(() => {
    const allSource = [...sourceContent, ...missingDetails];
    const sourceIds = new Set(allSource.map((i) => String(i.id)));
    const result: ContentItem[] = allSource.map((item) => ({
      ...item,
      curation: curationMap.get(String(item.id)) ?? null,
    }));
    for (const rec of curationRecords) {
      if (!sourceIds.has(rec.content_id)) {
        result.push({
          id: rec.content_id,
          title: rec.title_override ?? undefined,
          curation: rec,
          created_at: rec.created_at,
        });
      }
    }
    return result;
  }, [sourceContent, missingDetails, curationRecords, curationMap]);

  const filteredItems = useMemo(() => {
    let items = mergedItems;
    if (statusFilter !== "all") {
      items = items.filter((item) => {
        const curStatus = item.curation?.status ?? "published";
        return curStatus === statusFilter;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((item) => {
        const fields = [
          item.contact_name, item.business_name, item.call_summary,
          item.quote, item.title, item.summary, item.account, item.author_name,
          item.note, item.transcript_excerpt,
          item.curation?.title_override, item.curation?.description_override,
          item.id,
        ];
        return fields.some((f) => f && f.toLowerCase().includes(q));
      });
    }
    return items;
  }, [mergedItems, statusFilter, searchQuery]);

  const selectedItem = useMemo(
    () => filteredItems.find((i) => String(i.id) === selectedId) ?? null,
    [filteredItems, selectedId],
  );

  // Compute stats from merged items so items without curation records
  // count as "published" (matching the UI default).
  const computedStats = useMemo(() => {
    const s = { draft: 0, published: 0, hidden: 0, archived: 0, featured: 0 };
    for (const item of mergedItems) {
      const st = (item.curation?.status as CurationStatus) ?? "published";
      if (s[st] !== undefined) s[st]++;
      if (item.curation?.featured) s.featured++;
    }
    return s;
  }, [mergedItems]);

  // ── Mutations ─────────────────────────────────────────────────────────

  // Abort controller ref — each new status/featured patch aborts the previous in-flight
  // request so rapid clicks (hide → publish → hide) always land the LAST value.
  const patchAbortRef = useRef<AbortController | null>(null);

  const patchMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      // Abort any previous in-flight patch for the same item
      if (patchAbortRef.current) patchAbortRef.current.abort();
      const controller = new AbortController();
      patchAbortRef.current = controller;

      const res = await fetch(`/api/curation/${activeType}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Failed");
      return res.json();
    },
    onMutate: async ({ id, patch }) => {
      // Optimistically update curation records cache so list + detail update instantly
      await qc.cancelQueries({ queryKey: ["curation-records", activeType] });
      const prev = qc.getQueryData<CurationRecord[]>(["curation-records", activeType]);
      if (prev) {
        qc.setQueryData(["curation-records", activeType], prev.map((r) =>
          r.content_id === id ? { ...r, ...patch } as CurationRecord : r
        ));
      }
      return { prev };
    },
    onError: (e, _vars, ctx) => {
      // Don't rollback on abort — a newer request superseded this one
      if (e instanceof DOMException && e.name === "AbortError") return;
      if (ctx?.prev) qc.setQueryData(["curation-records", activeType], ctx.prev);
    },
    onSettled: (_d, e) => {
      // Don't refetch if this request was aborted — the newer one will handle it
      if (e instanceof DOMException && e.name === "AbortError") return;
      qc.invalidateQueries({ queryKey: ["curation-records", activeType] });
      qc.invalidateQueries({ queryKey: ["curation-stats", activeType] });
      qc.invalidateQueries({ queryKey: ["admin-source", activeType] });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: async ({ ids, action, featured, featured_label }: {
      ids: string[]; action?: string; featured?: boolean; featured_label?: string | null;
    }) => {
      const res = await fetch(`/api/curation/${activeType}/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, action, featured, featured_label }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["curation-records", activeType] });
      qc.invalidateQueries({ queryKey: ["curation-stats", activeType] });
      qc.invalidateQueries({ queryKey: ["admin-source", activeType] });
      setSelectedIds(new Set());
    },
  });

  // ── Selection helpers ─────────────────────────────────────────────────

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map((i) => String(i.id))));
    }
  }, [selectedIds, filteredItems]);

  // ── Render ────────────────────────────────────────────────────────────

  const activeTypeConfig = CONTENT_TYPES.find((t) => t.key === activeType)!;
  const totalItems = computedStats.published + computedStats.draft + computedStats.hidden + computedStats.archived;
  const initials = userName ? userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "A";

  return (
    <div className="min-h-screen flex">
      {/* ── Sidebar ── */}
      <aside className={`${sidebarOpen ? "w-60" : "w-14"} flex-shrink-0 bg-bk-white border-r border-bk-border flex flex-col transition-all duration-200`}>
        {/* Brand */}
        <div className={`${sidebarOpen ? "px-5" : "px-2"} py-4 border-b border-bk-border`}>
          {sidebarOpen && (
            <a
              href={window.location.pathname + "?page=calls"}
              className="inline-flex items-center gap-1 text-bk-2xs text-bk-muted hover:text-bk-heading transition mb-3"
            >
              <ChevronLeft className="w-3 h-3" /> Back to app
            </a>
          )}
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              className="w-8 h-8 rounded-bk-md bg-bk-navy text-bk-white grid place-items-center flex-shrink-0 hover:opacity-90 transition"
            >
              <ShieldCheck className="w-4 h-4" />
            </button>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <h1 className="text-bk-sm font-semibold text-bk-heading leading-tight">Admin</h1>
                <p className="text-bk-2xs text-bk-muted">Content curation</p>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className={`flex-1 ${sidebarOpen ? "px-3" : "px-1.5"} py-4`}>
          {sidebarOpen && <p className="adm-overline px-2 mb-2">Content</p>}
          <div className="space-y-0.5">
            {CONTENT_TYPES.map((ct) => {
              const Icon = ct.icon;
              const active = activeType === ct.key;
              return (
                <button
                  key={ct.key}
                  onClick={() => { setActiveView("content"); setActiveType(ct.key); setSelectedId(null); setSelectedIds(new Set()); setSearchQuery(""); setStatusFilter("all"); }}
                  title={ct.label}
                  data-active={active}
                  className={`adm-nav-item ${sidebarOpen ? "gap-2.5 px-3" : "justify-center px-0"}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {sidebarOpen && <span>{ct.label}</span>}
                  {sidebarOpen && active ? (
                    <span className="adm-nav-count ml-auto text-bk-3xs font-medium tabular-nums">
                      {totalItems}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          {isAdmin && sidebarOpen && (
            <>
              <p className="adm-overline px-2 mb-2 mt-4">Settings</p>
              <button
                onClick={() => setActiveView("access")}
                data-active={activeView === "access"}
                className="adm-nav-item gap-2.5 px-3"
              >
                <Users className="w-4 h-4 flex-shrink-0" /> Access Control
              </button>
            </>
          )}
          {isAdmin && !sidebarOpen && (
            <button
              onClick={() => setActiveView("access")}
              title="Access Control"
              data-active={activeView === "access"}
              className="adm-nav-item justify-center"
            >
              <Users className="w-4 h-4" />
            </button>
          )}
        </nav>

        {/* Stats */}
        {sidebarOpen ? (
          <div className="mx-3 mb-3 rounded-bk-lg bg-bk-page border border-bk-border p-3 space-y-1.5">
            <p className="adm-overline mb-1">Overview</p>
            {(["published", "draft", "hidden", "archived", "featured"] as const).map((key) => {
              const tone: Tone = key === "featured" ? "brand" : STATUS_CONFIG[key].tone;
              return (
                <div key={key} className="flex items-center justify-between text-bk-2xs">
                  <span className="flex items-center gap-1.5 text-bk-muted capitalize" data-tone={tone}>
                    <span className="adm-dot" />
                    {key}
                  </span>
                  <span className="text-bk-heading font-semibold tabular-nums">{computedStats[key]}</span>
                </div>
              );
            })}
          </div>
        ) : null}

        {/* User */}
        <div className={`${sidebarOpen ? "px-4" : "px-2"} py-3 border-t border-bk-border flex ${sidebarOpen ? "items-center gap-2.5" : "flex-col items-center gap-2"}`}>
          <div className="w-7 h-7 rounded-full bg-bk-navy text-bk-white grid place-items-center text-bk-3xs font-semibold flex-shrink-0">
            {initials}
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-bk-2xs font-semibold text-bk-heading truncate">{userName}</p>
              <p className="text-bk-3xs text-bk-muted truncate">{userEmail}</p>
            </div>
          )}
          <a href="/auth/logout" title="Sign out" className="adm-icon-btn">
            <LogOut className="w-3.5 h-3.5" />
          </a>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {activeView === "access" ? (
          <AccessPanel />
        ) : (
        <>
        {/* Header */}
        <header className="bg-bk-white border-b border-bk-border px-6 h-14 flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <activeTypeConfig.icon className="w-4 h-4 text-bk-muted" />
            <h2 className="text-bk-md font-semibold text-bk-heading">{activeTypeConfig.label}</h2>
          </div>

          <div className="flex-1 max-w-sm relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bk-muted" />
            <input
              type="text"
              placeholder={`Search ${activeTypeConfig.label.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="adm-input pl-9 pr-3 h-9"
            />
          </div>

          <div className="relative">
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="adm-btn px-3 font-medium"
            >
              {statusFilter !== "all" && <span className="adm-dot" data-tone={STATUS_CONFIG[statusFilter].tone} />}
              {statusFilter === "all" ? "All statuses" : STATUS_CONFIG[statusFilter].label}
              <ChevronDown className="!w-3 !h-3 text-bk-muted" />
            </button>
            {showStatusDropdown && (
              <div className="absolute right-0 top-full mt-1.5 w-44 bg-bk-white border border-bk-border rounded-bk-lg shadow-bk-dropdown z-50 py-1.5">
                <button onClick={() => { setStatusFilter("all"); setShowStatusDropdown(false); }}
                  className={`adm-menu-item w-full text-left px-3 py-2 text-bk-xs ${statusFilter === "all" ? "text-bk-heading font-semibold" : "text-bk-body"}`}>
                  All statuses
                </button>
                {(Object.keys(STATUS_CONFIG) as CurationStatus[]).map((s) => (
                  <button key={s} onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); }}
                    className={`adm-menu-item w-full text-left px-3 py-2 text-bk-xs flex items-center gap-2 ${statusFilter === s ? "text-bk-heading font-semibold" : "text-bk-body"}`}>
                    <span className="adm-dot" data-tone={STATUS_CONFIG[s].tone} />
                    {STATUS_CONFIG[s].label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <span className="text-bk-2xs text-bk-muted tabular-nums">{filteredItems.length} items</span>
        </header>

        {/* Content area */}
        <div className="flex-1 flex min-h-0">
          {/* ── List panel ── */}
          <div className={`${selectedItem ? "w-[45%]" : "flex-1"} border-r border-bk-border flex flex-col bg-bk-white`}>
            {/* Bulk bar */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-2.5 bg-bk-navy text-bk-white flex items-center gap-2 text-bk-2xs">
                <span className="font-semibold">{selectedIds.size} selected</span>
                <div className="flex-1" />
                <button onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: "published" })}
                  className="adm-btn adm-btn-on-dark">
                  <Eye /> Publish
                </button>
                <button onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: "hidden" })}
                  className="adm-btn adm-btn-on-dark">
                  <EyeOff /> Hide
                </button>
                <button onClick={() => bulkMutation.mutate({ ids: [...selectedIds], action: "archived" })}
                  className="adm-btn adm-btn-on-dark">
                  <Archive /> Archive
                </button>
                <button onClick={() => bulkMutation.mutate({ ids: [...selectedIds], featured: true })}
                  className="adm-btn adm-btn-on-dark">
                  <Star /> Feature
                </button>
                <button onClick={() => setSelectedIds(new Set())} title="Clear selection" className="adm-btn adm-btn-on-dark w-[30px] !px-0 !border-transparent">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Table header */}
            <div className="adm-overline px-4 h-10 border-b border-bk-border flex items-center gap-3 bg-bk-page">
              <button onClick={toggleSelectAll} className="flex-shrink-0">
                {selectedIds.size === filteredItems.length && filteredItems.length > 0
                  ? <CheckSquare className="w-4 h-4 text-bk-heading" />
                  : selectedIds.size > 0
                    ? <MinusSquare className="w-4 h-4 text-bk-interactive" />
                    : <Square className="w-4 h-4 text-bk-gray-3" />}
              </button>
              <span className="flex-1">Content</span>
              <span className="w-28 text-center">Status</span>
              <span className="w-16 text-center">Featured</span>
              <span className="w-28 text-right">Date</span>
            </div>

            {/* Items */}
            <div ref={listRef} className="flex-1 overflow-y-auto">
              {sourceLoading ? (
                <div className="py-16 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-bk-muted" />
                  <p className="text-bk-2xs text-bk-muted mt-2">Loading content...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="py-16 text-center">
                  <Search className="w-6 h-6 mx-auto text-bk-gray-3 mb-2" />
                  <p className="text-bk-xs text-bk-muted">No {activeTypeConfig.label.toLowerCase()} found</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <ContentRow
                    key={item.id}
                    item={item}
                    contentType={activeType}
                    isSelected={selectedIds.has(String(item.id))}
                    isActive={String(item.id) === selectedId}
                    onToggleSelect={() => toggleSelect(String(item.id))}
                    onClick={() => setSelectedId(String(item.id))}
                  />
                ))
              )}
            </div>
          </div>

          {/* ── Detail panel ── */}
          {selectedItem && (
            <DetailPanel
              item={selectedItem}
              contentType={activeType}
              isAdmin={isAdmin}
              onClose={() => setSelectedId(null)}
              onPatch={(patch) => patchMutation.mutate({ id: String(selectedItem.id), patch })}
              isPending={patchMutation.isPending}
            />
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ── Access Control Panel ──────────────────────────────────────────────────

interface AccessRow {
  email: string;
  role: string;
  added_by: string;
  added_at: string;
}

const ROLE_CONFIG: Record<string, { label: string; tone: Tone }> = {
  admin: { label: "Admin", tone: "brand" },
  clip_creator: { label: "Clip Creator", tone: "teal" },
  curator: { label: "Curator", tone: "violet" },
  // Was amber; the brand kit has no amber, so mid-blue until it does.
  visibility_tagger: { label: "Visibility Tagger", tone: "info" },
};

function AccessPanel() {
  const qc = useQueryClient();
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("clip_creator");
  const [search, setSearch] = useState("");

  const { data: rows = [], isLoading } = useQuery<AccessRow[]>({
    queryKey: ["access-control"],
    queryFn: async () => {
      const res = await fetch("/api/access-control");
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
  });

  const addMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const res = await fetch("/api/access-control", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-control"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      setNewEmail("");
    },
  });

  const removeMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) => {
      const res = await fetch("/api/access-control", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Failed");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-control"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  // Group by email
  const grouped = useMemo(() => {
    const map = new Map<string, { roles: AccessRow[]; addedBy: string }>();
    for (const r of rows) {
      if (!map.has(r.email)) map.set(r.email, { roles: [], addedBy: r.added_by });
      map.get(r.email)!.roles.push(r);
    }
    let entries = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    if (search.trim()) {
      const q = search.toLowerCase();
      entries = entries.filter(([email]) => email.includes(q));
    }
    return entries;
  }, [rows, search]);

  return (
    <div className="flex flex-col h-full bg-bk-white">
      {/* Header */}
      <header className="bg-bk-white border-b border-bk-border px-6 h-14 flex items-center gap-4">
        <div className="flex items-center gap-2.5">
          <Users className="w-4 h-4 text-bk-muted" />
          <h2 className="text-bk-md font-semibold text-bk-heading">Access Control</h2>
        </div>
        <div className="flex-1 max-w-sm relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bk-muted" />
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="adm-input pl-9 pr-3 h-9"
          />
        </div>
        <span className="text-bk-2xs text-bk-muted tabular-nums">{grouped.length} users</span>
      </header>

      {/* Add role form */}
      <div className="px-6 py-3 border-b border-bk-border bg-bk-page flex items-center gap-3">
        <input
          type="email"
          placeholder="email@fello.ai"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          className="adm-input flex-1 max-w-xs px-3.5 h-9"
        />
        <select
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          className="adm-input !w-auto px-3 h-9"
        >
          {Object.entries(ROLE_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button
          onClick={() => { if (newEmail.trim()) addMutation.mutate({ email: newEmail.trim(), role: newRole }); }}
          disabled={!newEmail.trim() || addMutation.isPending}
          className="adm-btn adm-btn-primary"
        >
          <Plus /> Add
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-bk-muted" />
          </div>
        ) : grouped.length === 0 ? (
          <div className="py-16 text-center text-bk-xs text-bk-muted">No users found</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="adm-overline bg-bk-page">
                <th className="text-left px-6 py-2.5">Email</th>
                <th className="text-left px-6 py-2.5">Roles</th>
                <th className="text-left px-6 py-2.5">Added by</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map(([email, { roles, addedBy }]) => (
                <tr key={email} className="adm-row border-b border-bk-border">
                  <td className="px-6 py-3 text-bk-xs text-bk-heading font-semibold">{email}</td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {roles.map((r) => {
                        const cfg = ROLE_CONFIG[r.role] ?? { label: r.role, tone: "neutral" as Tone };
                        return (
                          <span key={r.role} className="adm-badge" data-tone={cfg.tone}>
                            {cfg.label}
                            <button
                              onClick={() => removeMutation.mutate({ email, role: r.role })}
                              className="hover:opacity-70 transition"
                              title={`Remove ${cfg.label} from ${email}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-bk-2xs text-bk-muted">{addedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Content Row ─────────────────────────────────────────────────────────────

function ContentRow({ item, contentType, isSelected, isActive, onToggleSelect, onClick }: {
  item: ContentItem;
  contentType: ContentType;
  isSelected: boolean;
  isActive: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  // Scroll the selected row to top of list when detail panel opens
  if (isActive && rowRef.current) {
    rowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }
  const curStatus: CurationStatus = (item.curation?.status as CurationStatus) ?? "published";
  const featured = item.curation?.featured ?? false;

  let primaryText = "";
  let secondaryText = "";

  if (contentType === "call") {
    primaryText = item.contact_name || item.business_name || "Unknown";
    secondaryText = truncate(item.call_summary, 80) || (item.duration ? `${formatDuration(item.duration)} call` : "");
  } else if (contentType === "win") {
    primaryText = item.curation?.title_override || item.title || truncate(item.quote, 60) || "Untitled";
    secondaryText = [item.account, item.author_name, item.platform].filter(Boolean).join(" · ");
  } else if (contentType === "clip") {
    primaryText = item.curation?.title_override || item.title || "Untitled clip";
    secondaryText = [item.contact_name, item.business_name].filter(Boolean).join(" · ") || truncate(item.transcript_excerpt, 80) || "";
  }

  return (
    <div
      ref={rowRef}
      data-active={isActive}
      className="adm-row group flex items-center gap-3 px-4 py-3 border-b border-bk-border cursor-pointer"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
        className="flex-shrink-0"
      >
        {isSelected
          ? <CheckSquare className="w-4 h-4 text-bk-heading" />
          : <Square className="w-4 h-4 text-bk-gray-3 group-hover:text-bk-muted transition" />}
      </button>

      <div className="flex-1 min-w-0" onClick={onClick}>
        <p className="text-bk-xs font-semibold text-bk-heading truncate">{primaryText}</p>
        {secondaryText && <p className="text-bk-2xs text-bk-muted truncate mt-0.5">{secondaryText}</p>}
      </div>

      <div className="w-28 flex justify-center" onClick={onClick}>
        {statusBadge(curStatus)}
      </div>

      <div className="w-16 flex justify-center" onClick={onClick}>
        {featured && <Star className="adm-star w-3.5 h-3.5" />}
      </div>

      <div className="w-28 text-right text-bk-2xs text-bk-muted tabular-nums" onClick={onClick}>
        {formatDate(item.created_at)}
      </div>
    </div>
  );
}

// ── Detail Panel ────────────────────────────────────────────────────────────

function DetailPanel({ item, contentType, onClose, onPatch, isPending }: {
  item: ContentItem;
  contentType: ContentType;
  isAdmin?: boolean;
  onClose: () => void;
  onPatch: (patch: Record<string, unknown>) => void;
  isPending: boolean;
}) {
  // Use server value directly — no stale optimistic state.
  // Track what we last requested so we can show it while the mutation is in-flight.
  const serverStatus: CurationStatus = (item.curation?.status as CurationStatus) ?? "published";
  const serverFeatured = item.curation?.featured ?? false;
  const [pendingStatus, setPendingStatus] = useState<CurationStatus | null>(null);
  const [pendingFeatured, setPendingFeatured] = useState<boolean | null>(null);

  // Once the server catches up to our pending value, clear the pending override.
  if (pendingStatus !== null && serverStatus === pendingStatus) setPendingStatus(null);
  if (pendingFeatured !== null && serverFeatured === pendingFeatured) setPendingFeatured(null);

  const curStatus = pendingStatus ?? serverStatus;
  const featured = pendingFeatured ?? serverFeatured;

  const [titleOverride, setTitleOverride] = useState(item.curation?.title_override ?? "");
  const [descOverride, setDescOverride] = useState(item.curation?.description_override ?? "");
  const [internalNotes, setInternalNotes] = useState(item.curation?.internal_notes ?? "");
  const [tagsInput, setTagsInput] = useState((item.curation?.tags_override ?? []).join(", "));
  const [categoryOverride, setCategoryOverride] = useState(item.curation?.category_override ?? "");
  const [featuredLabel, setFeaturedLabel] = useState<FeaturedLabel>(item.curation?.featured_label ?? null);

  const itemId = String(item.id);
  const [lastId, setLastId] = useState(itemId);
  if (itemId !== lastId) {
    setLastId(itemId);
    setPendingStatus(null);
    setPendingFeatured(null);
    setTitleOverride(item.curation?.title_override ?? "");
    setDescOverride(item.curation?.description_override ?? "");
    setInternalNotes(item.curation?.internal_notes ?? "");
    setTagsInput((item.curation?.tags_override ?? []).join(", "));
    setCategoryOverride(item.curation?.category_override ?? "");
    setFeaturedLabel(item.curation?.featured_label ?? null);
  }

  const handlePatch = (patch: Record<string, unknown>) => {
    if (patch.status) setPendingStatus(patch.status as CurationStatus);
    if (patch.featured !== undefined) setPendingFeatured(patch.featured as boolean);
    onPatch(patch);
  };

  const saveMetadata = () => {
    onPatch({
      title_override: titleOverride.trim() || null,
      description_override: descOverride.trim() || null,
      internal_notes: internalNotes.trim() || null,
      tags_override: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      category_override: categoryOverride.trim() || null,
      featured_label: featuredLabel,
    });
  };

  let previewTitle = "";
  let previewBody = "";
  if (contentType === "call") {
    previewTitle = item.contact_name || item.business_name || "Unknown call";
    previewBody = item.call_summary || "";
  } else if (contentType === "win") {
    previewTitle = item.title || "";
    previewBody = item.quote || "";
  } else if (contentType === "clip") {
    previewTitle = item.title || "Untitled clip";
    previewBody = item.transcript_excerpt || item.note || "";
  }

  return (
    <div className="flex-1 flex flex-col bg-bk-white min-w-0">
      {/* Detail header */}
      <div className="px-5 h-14 border-b border-bk-border flex items-center gap-3">
        <button onClick={onClose} title="Close" className="adm-icon-btn">
          <X className="w-4 h-4" />
        </button>
        <h3 className="text-bk-sm font-semibold text-bk-heading truncate flex-1">
          {item.curation?.title_override || previewTitle || `${contentType} ${item.id.slice(0, 8)}`}
        </h3>
        {statusBadge(curStatus)}
        {featured && <Star className="adm-star w-4 h-4" />}
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {curStatus !== "published" && (
            <button onClick={() => handlePatch({ status: "published" })} disabled={isPending}
              className="adm-btn" data-tone="success">
              <Eye /> Publish
            </button>
          )}
          {curStatus !== "hidden" && (
            <button onClick={() => handlePatch({ status: "hidden" })} disabled={isPending}
              className="adm-btn" data-tone="info">
              <EyeOff /> Hide
            </button>
          )}
          {curStatus !== "archived" && (
            <button onClick={() => handlePatch({ status: "archived" })} disabled={isPending}
              className="adm-btn" data-tone="error">
              <Archive /> Archive
            </button>
          )}
          {curStatus !== "draft" && (
            <button onClick={() => handlePatch({ status: "draft" })} disabled={isPending}
              className="adm-btn">
              Draft
            </button>
          )}
          <button
            onClick={() => handlePatch({ featured: !featured })}
            disabled={isPending}
            className="adm-btn"
            data-tone="brand"
          >
            <Star className={featured ? "adm-star" : ""} />
            {featured ? "Unfeature" : "Feature"}
          </button>
        </div>

        {/* Preview */}
        <section>
          <h4 className="adm-overline mb-2.5">Preview</h4>
          <div className="rounded-bk-lg border border-bk-border p-5 bg-bk-page">
            {previewTitle && <p className="text-bk-sm font-semibold text-bk-heading mb-1.5">{previewTitle}</p>}
            <p className="text-bk-xs text-bk-body leading-relaxed whitespace-pre-wrap">{truncate(previewBody, 500)}</p>
            {contentType === "call" && item.duration && (
              <p className="text-bk-2xs text-bk-muted mt-3">Duration: {formatDuration(item.duration)}</p>
            )}
            {contentType === "win" && item.media_url && (
              <img src={item.media_url} alt="" className="mt-4 rounded-bk-md max-h-48 object-cover" />
            )}
            {contentType === "call" && (
              <div className="mt-3 flex gap-4 text-bk-2xs text-bk-muted">
                {item.business_name && <span>Business: {item.business_name}</span>}
                {item.call_type && <span>Type: {item.call_type}</span>}
              </div>
            )}
          </div>
        </section>

        {/* Metadata editor */}
        <section>
          <h4 className="adm-overline mb-2.5">Metadata Overrides</h4>
          <div className="space-y-4">
            <FieldInput label="Title" value={titleOverride} onChange={setTitleOverride} placeholder={previewTitle || "Override title..."} />
            <div>
              <label className="adm-label">Description</label>
              <textarea
                value={descOverride} onChange={(e) => setDescOverride(e.target.value)}
                placeholder="Add a description..."
                rows={3}
                className="adm-input px-3.5 py-2.5 resize-none"
              />
            </div>
            <FieldInput label="Tags (comma separated)" value={tagsInput} onChange={setTagsInput} placeholder="cold-call, objection-handling, listing" />
            <FieldInput label="Category" value={categoryOverride} onChange={setCategoryOverride} placeholder="e.g. prospecting, follow-up" />
            <div>
              <label className="adm-label">Featured Label</label>
              <select
                value={featuredLabel ?? ""} onChange={(e) => setFeaturedLabel((e.target.value || null) as FeaturedLabel)}
                className="adm-input px-3.5 py-2.5"
              >
                {FEATURED_LABELS.map((fl) => (
                  <option key={fl.value ?? "none"} value={fl.value ?? ""}>{fl.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="adm-label !flex items-center gap-1.5">
                <StickyNote className="w-3 h-3 text-bk-muted" /> Internal Notes
                <span className="text-bk-3xs text-bk-muted font-normal ml-1">(admin only)</span>
              </label>
              <textarea
                value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Notes for the team..."
                rows={3}
                className="adm-input px-3.5 py-2.5 resize-none"
              />
            </div>
            <button onClick={saveMetadata} disabled={isPending}
              className="adm-btn adm-btn-primary px-5">
              {isPending ? <Loader2 className="animate-spin" /> : <Check />}
              Save Metadata
            </button>
          </div>
        </section>

        {/* Audit trail */}
        {item.curation && (
          <section>
            <h4 className="adm-overline mb-2.5">Audit Trail</h4>
            <div className="rounded-bk-lg border border-bk-border p-4 bg-bk-page space-y-2 text-bk-2xs text-bk-body">
              {item.curation.published_at && (
                <p>Published by <span className="font-semibold text-bk-heading">{item.curation.published_by}</span> on {formatDate(item.curation.published_at)}</p>
              )}
              {item.curation.hidden_at && (
                <p>Hidden by <span className="font-semibold text-bk-heading">{item.curation.hidden_by}</span> on {formatDate(item.curation.hidden_at)}</p>
              )}
              {item.curation.archived_at && (
                <p>Archived by <span className="font-semibold text-bk-heading">{item.curation.archived_by}</span> on {formatDate(item.curation.archived_at)}</p>
              )}
              <p className="text-bk-muted">
                Updated by {item.curation.updated_by} · {formatDate(item.curation.updated_at)}
              </p>
              <p className="text-bk-muted">
                Created by {item.curation.created_by} · {formatDate(item.curation.created_at)}
              </p>
            </div>
          </section>
        )}

        {/* ID reference */}
        <p className="text-bk-3xs text-bk-muted font-mono">{contentType}:{item.id}</p>
      </div>
    </div>
  );
}

// ── Shared field input ──────────────────────────────────────────────────────

function FieldInput({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div>
      <label className="adm-label">{label}</label>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="adm-input px-3.5 py-2.5"
      />
    </div>
  );
}
